var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CollisionFacet, CollisionShapeLocalTransformFacet, degToRad, DropOffMode, InteractionFacet, LocalTransformFacet, PrimitiveShape, ToolInfoFacet, UTILITY_ROOM_UID, worldToLocalTransform, DeleteModelOnDisconnectFacet, RemoveModelMessage, } from "flavr-common";
import { Matrix4, Quaternion, Vector3 } from "flavr-math";
import { modelPool } from "@modules/model";
import { busService } from "@modules/bus";
import { getLocalPlayerClientIdxOrThrow } from "@modules/player";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { renderer } from "@modules/rendering/RendererGlobals";
import { ToolBeltLoadingSpinner } from "@modules/blueprint/toolBelt/ToolBeltLoadingSpinner";
import { blueprintLoadingService } from "@modules/blueprint/BlueprintLoadingService";
import { ToolBeltPickupAction } from "@modules/interaction";
const DESKTOP_HEIGHT = 0.6;
const VR_HEIGHT = DESKTOP_HEIGHT;
const DESKTOP_DISTANCE = 0;
const VR_DISTANCE = DESKTOP_DISTANCE;
const TOOL_BELT_FORWARD = new Vector3(0, 0, -1);
const TOOL_BELT_HORIZONTAL_FORWARD = new Vector3();
const TOOL_BELT_TARGET_FORWARD = new Vector3();
const TOOL_BELT_WORLD_POSITION = new Vector3();
const TOOL_BELT_WORLD_TRANSFORM = new Matrix4();
const TOOL_BELT_PARENT_WORLD_TRANSFORM = new Matrix4();
const TOOL_BELT_LOCAL_TRANSFORM = new Matrix4();
const TOOL_BELT_UNIT_SCALE = new Vector3(1, 1, 1);
const TOOL_BELT_OFFSET_VECTOR = new Vector3();
const TOOL_BELT_TARGET_ORIENTATION = new Quaternion();
const BELT_RECENTER_START_ANGLE = degToRad(40);
const BELT_RECENTER_STOP_ANGLE = degToRad(8);
const BELT_RECENTER_SMOOTHING = 0.12;
export class ToolBelt {
    constructor() {
        // Tool ring parameters
        this.ringRadiusDesktop = 0.35;
        this.ringRadiusVR = this.ringRadiusDesktop;
        this.startAngle = degToRad(140);
        this.maxAngle = degToRad(120);
        this.toolInfoMap = new Map();
        this.toolMap = new Map();
        this.modelUidToolMap = new Map();
        this.toolBeltOffset = new Vector3(0, -DESKTOP_HEIGHT, -DESKTOP_DISTANCE);
        this.beltYawOnlyOrientation = new Quaternion();
        this.isRecentering = false;
        this.connection = busService.connection("ToolBelt");
    }
    /// Getter and setter ///
    getToolByUID(uid) {
        return this.modelUidToolMap.get(uid);
    }
    isActive() {
        return this.toolBeltModel !== undefined;
    }
    switchMode(isVR) {
        this.toolBeltOffset.y = -(isVR ? VR_HEIGHT : DESKTOP_HEIGHT);
        this.toolBeltOffset.z = -(isVR ? VR_DISTANCE : DESKTOP_DISTANCE);
        this.updateTransform();
    }
    /// Adding and removing tool infos ///
    addBlueprintTool(name, label, fileId) {
        const toolInfo = { name, label, fileId };
        if (!this.toolInfoMap.has(name))
            this.toolInfoMap.set(name, toolInfo);
    }
    removeBlueprintTool(name, fileId) {
        if (name === undefined && fileId === undefined)
            return;
        if (name !== undefined) {
            this.toolInfoMap.delete(name);
        }
        else {
            const toolInfo = Array.from(this.toolInfoMap.values()).find(toolInfo => toolInfo.fileId === fileId);
            if (toolInfo !== undefined)
                this.toolInfoMap.delete(toolInfo.name);
        }
    }
    /// Toggle tool belt visibility ///
    toggleVisibility() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isActive())
                this.destroyBelt();
            else
                yield this.createBelt();
        });
    }
    show() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.createBelt();
        });
    }
    hide() {
        this.destroyBelt();
    }
    /// Belt creation ///
    createBelt() {
        return __awaiter(this, void 0, void 0, function* () {
            // We attach the belt to the player head and offset it to around the hips
            const currentRoom = modelPool.getRoomOrThrow(UTILITY_ROOM_UID);
            const clientIdx = getLocalPlayerClientIdxOrThrow();
            const beltModel = modelPool.createModel(`___ToolBelt_player${clientIdx}`, currentRoom);
            this.toolBeltModel = beltModel;
            beltModel.setFacet(new DeleteModelOnDisconnectFacet(clientIdx));
            this.resetBeltOrientation();
            this.updateTransform();
            beltModel.flushMessages();
            // Create the tools in a circle around the tool belt model
            yield this.createToolsInCircle(beltModel);
            // The belt might have been destroyed while the tools were still loading.
            if (this.toolBeltModel !== beltModel || !modelPool.hasModel(beltModel.getUID())) {
                return;
            }
            // Force an update of all tool transforms to make sure they are up-to-date
            beltModel.refreshTransform();
        });
    }
    /**
     * Manually correct the belt transform to be below camera in y-direction and also
     * only keep the rotation around the y-axis
     */
    updateTransform() {
        if (this.toolBeltModel === undefined)
            return;
        this.computeBeltWorldTransform(TOOL_BELT_WORLD_TRANSFORM);
        this.toolBeltModel.getParentOrThrow().getWorldTransform(TOOL_BELT_PARENT_WORLD_TRANSFORM);
        worldToLocalTransform(TOOL_BELT_WORLD_TRANSFORM, TOOL_BELT_PARENT_WORLD_TRANSFORM, TOOL_BELT_LOCAL_TRANSFORM);
        this.toolBeltModel.setTransform(TOOL_BELT_LOCAL_TRANSFORM, { updateFacet: true });
    }
    destroyBelt() {
        // Delete the belt together with all it's loaded tools
        if (this.toolBeltModel !== undefined) {
            this.connection.send(new RemoveModelMessage(this.toolBeltModel.getUID()));
            this.toolBeltModel = undefined;
        }
        this.isRecentering = false;
        // Clear the tool map
        this.toolMap.clear();
        this.modelUidToolMap.clear();
    }
    /// Tool creation ///
    createToolsInCircle(beltModel) {
        return __awaiter(this, void 0, void 0, function* () {
            let toolIndex = 0;
            const localPosition = [0, 0, 0];
            const loadingIndicators = [];
            for (let i = 0; i < this.toolInfoMap.size; i++) {
                this.getPositionInCircle(toolIndex, localPosition);
                const loadingIndicator = new ToolBeltLoadingSpinner(beltModel);
                loadingIndicator.create(localPosition);
                loadingIndicators.push(loadingIndicator);
                toolIndex++;
            }
            modelPool.flushMessages();
            yield new Promise((resolve) => { setTimeout(resolve, 1000); });
            toolIndex = 0;
            for (const toolInfo of this.toolInfoMap.values()) {
                if (this.toolBeltModel !== beltModel || !modelPool.hasModel(beltModel.getUID())) {
                    return;
                }
                this.getPositionInCircle(toolIndex, localPosition);
                yield this.createTool(beltModel, toolInfo, toolIndex, localPosition);
                loadingIndicators[toolIndex].destroy(1000);
                toolIndex++;
            }
        });
    }
    createTool(beltModel, info, idx, pos) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.toolBeltModel !== beltModel || !modelPool.hasModel(beltModel.getUID())) {
                return;
            }
            const toolRoot = modelPool.createModel(`__toolRoot_${idx}`, beltModel);
            const toolModel = modelPool.createModel(info.name, toolRoot);
            toolModel.setFacet(new ToolInfoFacet(info.name, info.label));
            this.createToolInteraction(toolRoot, toolModel, pos);
            modelPool.flushMessages();
            const parsedModels = (_a = yield blueprintLoadingService.parseFile(info.fileId, toolModel)) !== null && _a !== void 0 ? _a : [];
            if (!modelPool.hasModel(toolRoot.getUID()) || !modelPool.hasModel(toolModel.getUID())) {
                return;
            }
            this.updateToolInteractionCollision(toolRoot, toolModel);
            const tool = {
                modelUID: toolModel.getUID(),
                info,
                children: new Map()
            };
            const queuedModels = [...parsedModels];
            while (queuedModels.length > 0) {
                const model = queuedModels.shift();
                if (model === undefined)
                    continue;
                tool.children.set(model.getUID(), model);
                queuedModels.push(...model.getChildren());
            }
            modelPool.flushMessages();
            if (this.toolBeltModel !== beltModel || !modelPool.hasModel(beltModel.getUID())) {
                return;
            }
            this.toolMap.set(info.name, tool);
            this.modelUidToolMap.set(tool.modelUID, tool);
        });
    }
    createToolInteraction(interactionModel, toolModel, position) {
        const orientation = [0, 0, 0, 1];
        const scale = renderer.xr.isPresenting ? [1, 1, 1] : [1, 1, 1];
        interactionModel.setFacet(new LocalTransformFacet(position, orientation, scale));
        const offset = {
            position: [0, 0, 0],
            orientation: [0, 0, 0, 1],
            scale: [1, 1, 1]
        };
        // Keep the tool non-selectable until its blueprint finished loading.
        interactionModel.setFacet(new CollisionFacet(PrimitiveShape.Sphere, false));
        interactionModel.setFacet(new CollisionShapeLocalTransformFacet(new Matrix4().makeScale(0.2, 0.2, 0.2).toArray()));
        interactionModel.setFacet(new InteractionFacet({
            onClick: [
                new ToolBeltPickupAction(undefined, // we don't specify a parent because we want to use the controller that selected the model
                {
                    srcModelUid: toolModel.getUID(),
                    offset,
                    dropOffMode: DropOffMode.ALWAYS,
                    dropOffLayers: undefined,
                    showDropOffIcon: false,
                })
            ]
        }));
    }
    updateToolInteractionCollision(interactionModel, toolModel) {
        let collisionFacet = new CollisionFacet(PrimitiveShape.Sphere, true);
        let collisionLocalTransform = new Matrix4().makeScale(0.2, 0.2, 0.2);
        let foundCollisionShape = false;
        toolModel.traverse(model => {
            if (foundCollisionShape)
                return;
            const potentialCollisionFacet = model.getFacet(CollisionFacet);
            if (potentialCollisionFacet === undefined)
                return;
            const colliderLocalTransformFacet = model.getFacet(CollisionShapeLocalTransformFacet);
            const colliderLocalTransform = (colliderLocalTransformFacet !== undefined)
                ? new Matrix4().fromArray(colliderLocalTransformFacet.localTransform)
                : new Matrix4();
            collisionFacet = potentialCollisionFacet.clone();
            collisionFacet.isSelectable = true;
            collisionFacet.isTrigger = false;
            collisionLocalTransform = this.computeRelativeTransform(toolModel, model).multiply(colliderLocalTransform);
            foundCollisionShape = true;
        });
        interactionModel.setFacet(collisionFacet);
        interactionModel.setFacet(new CollisionShapeLocalTransformFacet(collisionLocalTransform.toArray()));
    }
    computeRelativeTransform(ancestorModel, descendantModel) {
        const transformChain = [];
        let currentModel = descendantModel;
        while (currentModel !== undefined && currentModel !== ancestorModel) {
            transformChain.push(currentModel);
            currentModel = currentModel.getParent();
        }
        const relativeTransform = new Matrix4();
        for (let i = transformChain.length - 1; i >= 0; i--) {
            const localTransformFacet = transformChain[i].getFacet(LocalTransformFacet);
            if (localTransformFacet === undefined)
                continue;
            relativeTransform.multiply(new Matrix4().compose(new Vector3().fromArray(localTransformFacet.position), new Quaternion().fromArray(localTransformFacet.orientation), new Vector3().fromArray(localTransformFacet.scale)));
        }
        return relativeTransform;
    }
    /// Belt placement ///
    // ToDo: It would be nicer to use a LinkToModelFacet and restrict the location to just below the player in y-direction
    //  as well as restricting the rotation to around the y-axis
    computeBeltWorldTransform(result) {
        this.updateBeltOrientation();
        TOOL_BELT_WORLD_POSITION
            .copy(localPlayerContainer.cameraWorldPosition)
            .add(TOOL_BELT_OFFSET_VECTOR.copy(this.toolBeltOffset).applyQuaternion(this.beltYawOnlyOrientation));
        result.compose(TOOL_BELT_WORLD_POSITION, this.beltYawOnlyOrientation, TOOL_BELT_UNIT_SCALE);
    }
    resetBeltOrientation() {
        this.computeTargetYawOrientation(this.beltYawOnlyOrientation);
        this.isRecentering = false;
    }
    updateBeltOrientation() {
        this.computeTargetYawOrientation(TOOL_BELT_TARGET_ORIENTATION);
        const currentForward = TOOL_BELT_HORIZONTAL_FORWARD
            .copy(TOOL_BELT_FORWARD)
            .applyQuaternion(this.beltYawOnlyOrientation);
        const targetForward = TOOL_BELT_TARGET_FORWARD
            .set(0, 0, -1)
            .applyQuaternion(TOOL_BELT_TARGET_ORIENTATION);
        const dot = Math.min(1, Math.max(-1, currentForward.dot(targetForward)));
        const angle = Math.acos(dot);
        if (!this.isRecentering && angle > BELT_RECENTER_START_ANGLE) {
            this.isRecentering = true;
        }
        else if (this.isRecentering && angle < BELT_RECENTER_STOP_ANGLE) {
            this.isRecentering = false;
            this.beltYawOnlyOrientation.copy(TOOL_BELT_TARGET_ORIENTATION);
            return;
        }
        if (this.isRecentering) {
            this.beltYawOnlyOrientation.slerp(TOOL_BELT_TARGET_ORIENTATION, BELT_RECENTER_SMOOTHING);
        }
    }
    computeTargetYawOrientation(result) {
        TOOL_BELT_HORIZONTAL_FORWARD
            .set(0, 0, -1)
            .applyQuaternion(localPlayerContainer.cameraWorldOrientation);
        TOOL_BELT_HORIZONTAL_FORWARD.y = 0;
        if (TOOL_BELT_HORIZONTAL_FORWARD.lengthSq() < 1e-9) {
            TOOL_BELT_HORIZONTAL_FORWARD.copy(TOOL_BELT_FORWARD);
        }
        else {
            TOOL_BELT_HORIZONTAL_FORWARD.normalize();
        }
        result.setFromUnitVectors(TOOL_BELT_FORWARD, TOOL_BELT_HORIZONTAL_FORWARD);
    }
    /// Tool placement ///
    getPositionInCircle(index, result) {
        const angle = this.getItemAngle(index);
        result[0] = (renderer.xr.isPresenting ? this.ringRadiusVR : this.ringRadiusDesktop) * Math.sin(angle);
        result[1] = 0;
        result[2] = (renderer.xr.isPresenting ? this.ringRadiusVR : this.ringRadiusDesktop) * Math.cos(angle);
    }
    getItemAngle(index) {
        return this.startAngle + (index / Math.max(this.toolMap.size - 1, 3)) * this.maxAngle;
    }
}
